import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, Minus, Trash2, History, CheckCircle, XCircle, Sparkles, Loader2, Eye, TrendingUp, TrendingDown, ArrowRight, Users, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useLiveMode } from '../context/LiveModeContext';
import { StudentAvatar } from '../components/StudentAvatar';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const POSITIVE_QUICK = [
  'On-task / Engaged',
  'Positive Contribution',
  'Helpful / Cooperative',
  'Excellent Effort',
];

const NEGATIVE_QUICK = [
  'Talking While Teacher Speaking',
  'Off Task',
  'Out of Seat Without Permission',
];

const WATCH_FLAGS = [
  'Escalates quickly',
  'Responds well to praise',
  'Avoids writing tasks',
  'Needs extra time',
  'Separation anxiety',
  'Frequent bathroom breaks',
];

export const BehaviourPage = () => {
  const { classId } = useParams();
  const { session, refreshStats } = useLiveMode();
  const [logs, setLogs] = useState([]);
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(null);
  const [customNote, setCustomNote] = useState('');
  const [strategyOpen, setStrategyOpen] = useState(null);
  const [strategies, setStrategies] = useState('');
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [watchlist, setWatchlist] = useState({});
  const [watchOpen, setWatchOpen] = useState(null);
  const [watchNotes, setWatchNotes] = useState('');
  const [watchFlags, setWatchFlags] = useState([]);
  const [trends, setTrends] = useState(null);
  const [trendsOpen, setTrendsOpen] = useState(false);
  const [watchListOpen, setWatchListOpen] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkType, setBulkType] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [avatarTheme, setAvatarTheme] = useState('initials');
  const [applyingTheme, setApplyingTheme] = useState(false);

  const fetchData = async () => {
    try {
      const sessionParam = session?.session_id ? `?session_id=${session.session_id}` : '';
      const [logsRes, studentsRes, classesRes, watchRes, trendsRes] = await Promise.all([
        fetch(`${API}/behaviour/${classId}${sessionParam}`, { credentials: 'include' }),
        fetch(`${API}/students/${classId}`, { credentials: 'include' }),
        fetch(`${API}/classes`, { credentials: 'include' }),
        fetch(`${API}/watchlist/${classId}`, { credentials: 'include' }),
        fetch(`${API}/behaviour-trends/${classId}`, { credentials: 'include' }),
      ]);
      if (logsRes.ok) setLogs(await logsRes.json());
      if (studentsRes.ok) {
        const studentsData = await studentsRes.json();
        setStudents(studentsData);
        // Detect current theme from first student's avatar_icon
        if (studentsData.length > 0) {
          const icon = studentsData[0].avatar_icon || 'initials';
          const themes = { animals: ['bear','cat','dog','rabbit','bird','fish','turtle','owl','fox','penguin','butterfly','bee'],
            symbols: ['lightning','rocket','crown','music','puzzle','globe','shield','atom','flame','anchor','compass','key'],
            shapes: ['star','heart','diamond','circle','triangle','hexagon','moon','sun','cloud'],
            nature: ['leaf','flower','tree'] };
          const detected = Object.entries(themes).find(([, icons]) => icons.includes(icon));
          setAvatarTheme(detected ? detected[0] : 'initials');
        }
      }
      if (classesRes.ok) {
        const classes = await classesRes.json();
        setClassInfo(classes.find(c => c.class_id === classId));
      }
      if (watchRes.ok) {
        const entries = await watchRes.json();
        const map = {};
        entries.forEach(e => { map[e.student_id] = e; });
        setWatchlist(map);
      }
      if (trendsRes.ok) setTrends(await trendsRes.json());
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [classId]);

  const applyTheme = async (theme) => {
    setApplyingTheme(true);
    setAvatarTheme(theme);
    try {
      const r = await fetch(`${API}/students/class/${classId}/avatar-theme`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ theme })
      });
      if (r.ok) {
        const data = await r.json();
        setStudents(data.students);
        toast.success(`${theme === 'initials' ? 'Colour' : theme.charAt(0).toUpperCase() + theme.slice(1)} avatars applied`);
      } else { toast.error('Failed to apply theme'); }
    } catch { toast.error('Failed to apply theme'); }
    finally { setApplyingTheme(false); }
  };

  const submitLog = async (studentId, logType, description) => {
    try {
      const r = await fetch(`${API}/behaviour`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ student_id: studentId, log_type: logType, description, class_id: classId, period: session?.class_id === classId ? session.period : null, session_id: session?.session_id || null })
      });
      if (r.ok) { setOpenPopoverId(null); fetchData(); refreshStats(); }
      else toast.error('Failed to log');
    } catch (error) { toast.error('Failed to log'); }
  };

  const handleCustomSubmit = () => {
    if (!customOpen || !customNote.trim()) return;
    submitLog(customOpen.studentId, customOpen.type, customNote.trim());
    setCustomNote('');
    setCustomOpen(null);
  };

  const handleDeleteLog = async (logId) => {
    try {
      const r = await fetch(`${API}/behaviour/${logId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { fetchData(); refreshStats(); }
    } catch (error) { toast.error('Failed to delete'); }
  };

  const submitBulkLog = async (logType, description) => {
    if (bulkSelected.length === 0) return;
    let successCount = 0;
    for (const studentId of bulkSelected) {
      try {
        const r = await fetch(`${API}/behaviour`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ student_id: studentId, log_type: logType, description, class_id: classId, period: session?.class_id === classId ? session.period : null, session_id: session?.session_id || null })
        });
        if (r.ok) successCount++;
      } catch (e) { /* continue */ }
    }
    toast.success(`Logged for ${successCount} student${successCount !== 1 ? 's' : ''}`);
    setBulkDialogOpen(false);
    setBulkSelected([]);
    setBulkMode(false);
    setBulkType(null);
    fetchData();
    refreshStats();
  };

  const toggleBulkStudent = (studentId) => {
    setBulkSelected(prev => prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]);
  };

  const selectAllStudents = () => {
    if (bulkSelected.length === students.length) setBulkSelected([]);
    else setBulkSelected(students.map(s => s.student_id));
  };

  const getStudentName = (studentId) => students.find(s => s.student_id === studentId)?.name || 'Unknown';

  const fetchStrategies = async (studentId) => {
    setStrategyOpen(studentId);
    setStrategies('');
    setLoadingStrategies(true);
    try {
      const r = await fetch(`${API}/ai-behaviour-strategy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ class_id: classId, student_id: studentId })
      });
      if (r.ok) { const data = await r.json(); setStrategies(data.strategies); }
      else toast.error('Failed to get strategies');
    } catch (error) { toast.error('Failed to get strategies'); }
    finally { setLoadingStrategies(false); }
  };

  const getStudentScore = (studentId) => {
    const studentLogs = logs.filter(l => l.student_id === studentId);
    return studentLogs.filter(l => l.log_type === 'positive').length - studentLogs.filter(l => l.log_type === 'negative').length;
  };

  const getStudentTrend = (studentId) => {
    return trends?.students?.find(s => s.student_id === studentId)?.trend || null;
  };

  const openWatchEditor = (student) => {
    const existing = watchlist[student.student_id];
    setWatchNotes(existing?.notes || '');
    setWatchFlags(existing?.flags || []);
    setWatchOpen(student);
  };

  const handleSaveWatch = async () => {
    if (!watchOpen) return;
    try {
      const r = await fetch(`${API}/watchlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ student_id: watchOpen.student_id, notes: watchNotes, flags: watchFlags })
      });
      if (r.ok) { toast.success('Watch list updated'); setWatchOpen(null); fetchData(); }
      else toast.error('Failed to save');
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleRemoveWatch = async () => {
    if (!watchOpen) return;
    try {
      const r = await fetch(`${API}/watchlist/${watchOpen.student_id}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { toast.success('Removed from watch list'); setWatchOpen(null); fetchData(); }
    } catch (error) { toast.error('Failed to remove'); }
  };

  const toggleFlag = (flag) => {
    setWatchFlags(prev => prev.includes(flag) ? prev.filter(f => f !== flag) : [...prev, flag]);
  };

  const totalStats = {
    positive: logs.filter(l => l.log_type === 'positive').length,
    negative: logs.filter(l => l.log_type === 'negative').length,
  };

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Behaviour' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="space-y-5" data-testid="behaviour-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900">Behaviour</h1>
            <p className="text-slate-500 mt-1 text-sm">
              {classInfo?.name}
              <span className="mx-2">&middot;</span>
              <span className="text-emerald-600">+{totalStats.positive}</span>
              <span className="mx-1 text-slate-300">/</span>
              <span className="text-rose-500">-{totalStats.negative}</span>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={bulkMode ? 'default' : 'outline'}
              onClick={() => { setBulkMode(!bulkMode); setBulkSelected([]); setBulkType(null); }}
              className={bulkMode ? 'bg-teal-600 text-white rounded-xl text-sm' : 'border-slate-200 text-slate-600 rounded-xl text-sm'}
              data-testid="bulk-mode-btn"
            >
              <Users className="w-4 h-4 mr-2" />{bulkMode ? 'Cancel Bulk' : 'Bulk Log'}
            </Button>
            <Button variant="outline" onClick={() => setTrendsOpen(true)}
              className="border-slate-200 text-slate-600 rounded-xl text-sm" data-testid="trends-btn">
              <TrendingUp className="w-4 h-4 mr-2" />Trends
            </Button>
            <Button variant="outline" onClick={() => setWatchListOpen(true)}
              className="border-slate-200 text-slate-600 rounded-xl text-sm" data-testid="watchlist-btn">
              <Eye className="w-4 h-4 mr-2" />Watch List
            </Button>
            <Button variant="outline" onClick={() => setHistoryOpen(true)}
              className="border-slate-200 text-slate-600 rounded-xl text-sm" data-testid="history-btn">
              <History className="w-4 h-4 mr-2" />Log
            </Button>
          </div>
        </div>

        {/* Avatar Theme Selector */}
        {students.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="avatar-theme-selector">
            <Palette className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-400 font-medium flex-shrink-0">Style:</span>
            {[
              { id: 'initials', label: 'Colours', emoji: null },
              { id: 'animals', label: 'Animals', emoji: null },
              { id: 'symbols', label: 'Symbols', emoji: null },
              { id: 'shapes', label: 'Shapes', emoji: null },
              { id: 'nature', label: 'Nature', emoji: null },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => applyTheme(t.id)}
                disabled={applyingTheme}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                  avatarTheme === t.id
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                } ${applyingTheme ? 'opacity-60' : ''}`}
                data-testid={`theme-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Student tiles grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : students.length === 0 ? (
          <Card className="bg-white border border-dashed border-slate-300 shadow-none rounded-xl">
            <CardContent className="py-12 text-center">
              <p className="text-slate-400">No students in this class yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {students.map((student, index) => {
              const score = getStudentScore(student.student_id);
              const hasWatch = !!watchlist[student.student_id];
              const trend = getStudentTrend(student.student_id);
              const isBulkSelected = bulkSelected.includes(student.student_id);

              return (
                <div
                  key={student.student_id}
                  className={`bg-white rounded-xl border p-3 flex flex-col items-center text-center hover:shadow-google-card transition-all animate-fade-in relative ${
                    bulkMode && isBulkSelected ? 'border-teal-400 ring-2 ring-teal-200' : 'border-slate-200'
                  } ${bulkMode ? 'cursor-pointer' : ''}`}
                  style={{ animationDelay: `${index * 20}ms` }}
                  onClick={bulkMode ? () => toggleBulkStudent(student.student_id) : undefined}
                  data-testid={`student-tile-${student.student_id}`}
                >
                  {/* Bulk select indicator */}
                  {bulkMode && (
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isBulkSelected ? 'bg-teal-600 border-teal-600' : 'border-slate-300 bg-white'
                    }`} data-testid={`bulk-check-${student.student_id}`}>
                      {isBulkSelected && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                  )}

                  {/* Avatar */}
                  <StudentAvatar student={student} size="lg" className="mb-2" />

                  {/* Name */}
                  <p className="text-sm font-medium text-slate-800 mb-1 truncate w-full">{student.name}</p>

                  {/* Score */}
                  <p className={`text-lg font-bold mb-2 ${score > 0 ? 'text-emerald-600' : score < 0 ? 'text-rose-500' : 'text-slate-400'}`}
                    data-testid={`score-${student.student_id}`}
                  >
                    {score > 0 ? `+${score}` : score}
                  </p>

                  {/* +/- buttons (hidden in bulk mode) */}
                  {!bulkMode && (
                    <div className="flex gap-2 w-full">
                      <Popover open={openPopoverId === `pos_${student.student_id}`} onOpenChange={(open) => setOpenPopoverId(open ? `pos_${student.student_id}` : null)}>
                        <PopoverTrigger asChild>
                          <button className="flex-1 h-10 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 active:bg-emerald-200 transition-colors flex items-center justify-center" data-testid={`plus-btn-${student.student_id}`}>
                            <Plus className="w-5 h-5" strokeWidth={3} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2 bg-white" side="top" align="center">
                          <div className="space-y-1">
                            {POSITIVE_QUICK.map(label => (
                              <button key={label} className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-emerald-50 transition-colors flex items-center gap-2" onClick={() => submitLog(student.student_id, 'positive', label)}>
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{label}
                              </button>
                            ))}
                            <button className="w-full text-left px-3 py-2 rounded-lg text-sm text-teal-600 hover:bg-teal-50 transition-colors" onClick={() => { setOpenPopoverId(null); setCustomOpen({ studentId: student.student_id, type: 'positive' }); }}>
                              + Other...
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>

                      <Popover open={openPopoverId === `neg_${student.student_id}`} onOpenChange={(open) => setOpenPopoverId(open ? `neg_${student.student_id}` : null)}>
                        <PopoverTrigger asChild>
                          <button className="flex-1 h-10 rounded-lg bg-rose-50 border border-rose-200 text-rose-500 hover:bg-rose-100 active:bg-rose-200 transition-colors flex items-center justify-center" data-testid={`minus-btn-${student.student_id}`}>
                            <Minus className="w-5 h-5" strokeWidth={3} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2 bg-white" side="top" align="center">
                          <div className="space-y-1">
                            {NEGATIVE_QUICK.map(label => (
                              <button key={label} className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-rose-50 transition-colors flex items-center gap-2" onClick={() => submitLog(student.student_id, 'negative', label)}>
                                <XCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />{label}
                              </button>
                            ))}
                            <button className="w-full text-left px-3 py-2 rounded-lg text-sm text-teal-600 hover:bg-teal-50 transition-colors" onClick={() => { setOpenPopoverId(null); setCustomOpen({ studentId: student.student_id, type: 'negative' }); }}>
                              + Other...
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Custom note dialog */}
        <Dialog open={!!customOpen} onOpenChange={(open) => { if (!open) { setCustomOpen(null); setCustomNote(''); } }}>
          <DialogContent className="bg-white max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900">
                {customOpen?.type === 'positive' ? 'Positive' : 'Negative'} Note
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <p className="text-sm text-slate-500">{customOpen && getStudentName(customOpen.studentId)}</p>
              <Input autoFocus placeholder="Enter behaviour note..." value={customNote} onChange={(e) => setCustomNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleCustomSubmit(); }} className="border-slate-200 rounded-xl" data-testid="custom-note-input" />
              <Button onClick={handleCustomSubmit} disabled={!customNote.trim()} className={`w-full text-white rounded-xl ${customOpen?.type === 'positive' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-500 hover:bg-rose-600'}`} data-testid="custom-note-submit">Log</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* History dialog */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900">Today's Log ({logs.length})</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-2 pt-2">
              {logs.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No logs yet today</p>
              ) : (
                logs.map((log) => (
                  <div key={log.log_id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${log.log_type === 'positive' ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`} data-testid={`log-${log.log_id}`}>
                    {log.log_type === 'positive' ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{getStudentName(log.student_id)}</p>
                      <p className="text-xs text-slate-500 truncate">{log.description}</p>
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">{log.timestamp && format(new Date(log.timestamp), 'h:mm a')}</span>
                    <button onClick={() => handleDeleteLog(log.log_id)} className="text-slate-300 hover:text-rose-500 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Strategy Dialog */}
        <Dialog open={!!strategyOpen} onOpenChange={(open) => { if (!open) setStrategyOpen(null); }}>
          <DialogContent className="bg-white max-w-lg rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal-500" />AI Behaviour Strategies
              </DialogTitle>
            </DialogHeader>
            <div className="pt-2">
              <p className="text-sm text-slate-500 mb-3">Suggested strategies for {strategyOpen && getStudentName(strategyOpen)}</p>
              {loadingStrategies ? (
                <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating strategies...</div>
              ) : (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="strategy-content">{strategies}</div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Watch List Dialog */}
        <Dialog open={!!watchOpen} onOpenChange={(open) => { if (!open) setWatchOpen(null); }}>
          <DialogContent className="bg-white max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-amber-500" />Watch List
              </DialogTitle>
            </DialogHeader>
            {watchOpen && (
              <div className="space-y-4 pt-2">
                <p className="text-sm text-slate-500">{watchOpen.name}</p>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-2 block">Quick Flags</label>
                  <div className="flex flex-wrap gap-2">
                    {WATCH_FLAGS.map(flag => (
                      <button
                        key={flag}
                        className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                          watchFlags.includes(flag) ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                        onClick={() => toggleFlag(flag)}
                        data-testid={`flag-${flag.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {flag}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Private Notes</label>
                  <Textarea placeholder="Your private observations about this student..." value={watchNotes} onChange={(e) => setWatchNotes(e.target.value)} className="mt-1.5 border-slate-200 rounded-xl min-h-[80px] text-sm" data-testid="watch-notes-input" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveWatch} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl" data-testid="save-watch-btn">Save</Button>
                  {watchlist[watchOpen.student_id] && (
                    <Button variant="outline" onClick={handleRemoveWatch} className="border-slate-200 text-slate-500 rounded-xl" data-testid="remove-watch-btn">Remove</Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Behaviour Trends Dialog */}
        <Dialog open={trendsOpen} onOpenChange={setTrendsOpen}>
          <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-teal-500" />Behaviour Trends (Teacher Only)
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4 pt-2" data-testid="trends-dialog">
              {!trends ? (
                <p className="text-center text-slate-400 py-8">Loading trends...</p>
              ) : (
                <>
                  {/* Summary stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 text-center">
                      <p className="text-2xl font-heading font-bold text-emerald-600">+{trends.total_positive}</p>
                      <p className="text-xs text-slate-500">Positive (30d)</p>
                    </div>
                    <div className="bg-rose-50 rounded-xl p-3 border border-rose-100 text-center">
                      <p className="text-2xl font-heading font-bold text-rose-500">-{trends.total_negative}</p>
                      <p className="text-xs text-slate-500">Negative (30d)</p>
                    </div>
                  </div>

                  {/* Needs Attention */}
                  {trends.needs_attention?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-rose-600 mb-2 flex items-center gap-1.5">
                        <TrendingDown className="w-3.5 h-3.5" />Needs Attention
                      </h3>
                      <div className="space-y-1.5">
                        {trends.needs_attention.map(s => (
                          <div key={s.student_id} className="flex items-center justify-between p-2.5 bg-rose-50 rounded-lg border border-rose-100">
                            <span className="text-sm font-medium text-slate-700">{s.student_name}</span>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-emerald-600">+{s.positive}</span>
                              <span className="text-rose-500">-{s.negative}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                s.trend === 'declining' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                              }`}>{s.trend}</span>
                              <button
                                className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 text-[10px] font-semibold hover:bg-indigo-200 transition-colors"
                                onClick={() => { setTrendsOpen(false); fetchStrategies(s.student_id); }}
                                data-testid={`strategy-btn-${s.student_id}`}
                              >Strategies</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Most Improved */}
                  {trends.most_improved?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-600 mb-2 flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5" />Most Improved
                      </h3>
                      <div className="space-y-1.5">
                        {trends.most_improved.map(s => (
                          <div key={s.student_id} className="flex items-center justify-between p-2.5 bg-emerald-50 rounded-lg border border-emerald-100">
                            <span className="text-sm font-medium text-slate-700">{s.student_name}</span>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-emerald-600">+{s.positive}</span>
                              <span className="text-rose-500">-{s.negative}</span>
                              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">improving</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Students */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">All Students ({trends.students?.length || 0})</h3>
                    <div className="space-y-1">
                      {trends.students?.map(s => (
                        <div key={s.student_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                          <span className="text-sm text-slate-700">{s.student_name}</span>
                          <div className="flex items-center gap-2 text-xs">
                            <span className={`font-semibold ${s.score > 0 ? 'text-emerald-600' : s.score < 0 ? 'text-rose-500' : 'text-slate-400'}`}>
                              {s.score > 0 ? `+${s.score}` : s.score}
                            </span>
                            {s.trend !== 'stable' && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                s.trend === 'improving' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                              }`}>{s.trend}</span>
                            )}
                            {s.score < 0 && (
                              <button
                                className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-600 text-[10px] font-medium hover:bg-teal-100 transition-colors"
                                onClick={() => { setTrendsOpen(false); fetchStrategies(s.student_id); }}
                              >Strategies</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Watch List Panel Dialog */}
        <Dialog open={watchListOpen} onOpenChange={setWatchListOpen}>
          <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-hidden flex flex-col rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-amber-500" />Watch List (Teacher Only)
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 pt-2" data-testid="watchlist-dialog">
              {Object.keys(watchlist).length === 0 ? (
                <div className="text-center py-8">
                  <Eye className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No students on watch list yet</p>
                  <p className="text-slate-400 text-xs mt-1">Add students below to track notes across visits</p>
                </div>
              ) : (
                Object.entries(watchlist).map(([sid, entry]) => (
                  <div key={sid} className="p-3 bg-amber-50 rounded-xl border border-amber-100" data-testid={`watchlist-entry-${sid}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-800">{getStudentName(sid)}</span>
                      <button className="text-xs text-teal-600 hover:text-teal-700" onClick={() => {
                        setWatchListOpen(false);
                        const student = students.find(s => s.student_id === sid);
                        if (student) openWatchEditor(student);
                      }}>Edit</button>
                    </div>
                    {entry.flags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {entry.flags.map(f => (
                          <span key={f} className="px-2 py-0.5 rounded text-[10px] bg-amber-200/60 text-amber-800">{f}</span>
                        ))}
                      </div>
                    )}
                    {entry.notes && <p className="text-xs text-slate-600">{entry.notes}</p>}
                  </div>
                ))
              )}
              {/* Add to watch list */}
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-medium text-slate-500 mb-2">Add a student</p>
                <div className="flex flex-wrap gap-1.5">
                  {students.filter(s => !watchlist[s.student_id]).map(s => (
                    <button
                      key={s.student_id}
                      className="px-2.5 py-1 rounded-lg text-xs bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                      onClick={() => { setWatchListOpen(false); openWatchEditor(s); }}
                      data-testid={`add-watch-${s.student_id}`}
                    >
                      + {s.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk action floating bar */}
        {bulkMode && bulkSelected.length > 0 && (
          <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white rounded-2xl shadow-lg border border-slate-200 p-3 flex items-center gap-3 animate-fade-in" data-testid="bulk-action-bar">
            <span className="text-sm text-slate-600 font-medium whitespace-nowrap">{bulkSelected.length} selected</span>
            <button onClick={selectAllStudents} className="text-xs text-teal-600 underline whitespace-nowrap" data-testid="bulk-select-all">
              {bulkSelected.length === students.length ? 'Deselect all' : 'Select all'}
            </button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
              onClick={() => { setBulkType('positive'); setBulkDialogOpen(true); }}
              data-testid="bulk-positive-btn"
            >
              <Plus className="w-4 h-4 mr-1" />Positive
            </Button>
            <Button
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
              onClick={() => { setBulkType('negative'); setBulkDialogOpen(true); }}
              data-testid="bulk-negative-btn"
            >
              <Minus className="w-4 h-4 mr-1" />Negative
            </Button>
          </div>
        )}

        {/* Bulk log dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!open) { setBulkDialogOpen(false); setBulkType(null); } }}>
          <DialogContent className="bg-white max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900">
                {bulkType === 'positive' ? 'Positive Log' : 'Behaviour Concern'} — {bulkSelected.length} students
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-1.5 pt-2" data-testid="bulk-log-dialog">
              {(bulkType === 'positive' ? POSITIVE_QUICK : NEGATIVE_QUICK).map(label => (
                <button
                  key={label}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                    bulkType === 'positive'
                      ? 'text-slate-700 hover:bg-emerald-50 border border-slate-100'
                      : 'text-slate-700 hover:bg-rose-50 border border-slate-100'
                  }`}
                  onClick={() => submitBulkLog(bulkType, label)}
                  data-testid={`bulk-option-${label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {bulkType === 'positive' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-rose-500" />}
                  {label}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
